import { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from "react-router-dom";

import {
    Typography,
    Box,
    Stack,
} from '@mui/material';

import TableComponent from "../../../shared/table/table";

import {theme} from '../../../../configs/theme';

import css from '../../../../styles/portal/main.module.css';
import EditMailingInfo from './mailingInfo/editMailingInfo';
import getApiData from '../../../../hooks/apiRequests/getRequest';
import { useToast } from '../../../shared/toastMessages/toastProvider';
import putApiData from '../../../../hooks/apiRequests/putRequest';
import ManualAdd from './editList/manualAdd';
import ModalComponent from '../../../shared/modal/modal';
import { normalizePageParam } from '../../../../hooks/pagination';


const EditMalingList = () =>{
    const [searchParams, setSearchParams] = useSearchParams();
    const [open, setOpen] = useState();
    const [data, setData] = useState();
    const [progress, setProgress] = useState(false);
    const [editStatus, setEditStatus] = useState(false);
    const [mailingInfo, setMailingInfo] = useState();
    const [selectedUser, setSelectedUser] = useState(null);
    const { showToast } = useToast();
    const { id } = useParams();
    const query = searchParams.get('query') || '';
    const page = normalizePageParam(searchParams.get('page'));

    const fetchContacts = useCallback(async () => {
      setProgress(true);
      try {
        const response = await getApiData(
          `/List/Search?Id=${id}&Search_filter=${encodeURIComponent(query)}&page_number=${page}`
        );
        if (!response?.data) {
          throw new Error('Empty mailing contacts response');
        }
        setData(response.data);
      } catch (error) {
        console.error("Error fetching data:", error);
        showToast("Failed to load data. Redirecting to login...", "error");
      } finally {
        setProgress(false);
      }
    }, [id, query, page, showToast]);

    useEffect(() => {
      fetchContacts();
    }, [fetchContacts]);

    useEffect(() => {
      const fetchData = async () => {
        try {
          const response = await getApiData(`/Emailling/Specific?MailingId=${id}`);
          setMailingInfo(response.data);
        } catch (error) {
          console.error("Error fetching data:", error);
          showToast("Failed to load data. Redirecting to login...", "error");
        }
      };
    
      fetchData();
    }, [ id, showToast]);

    const editMailingInfo = async (body)=>{
      try{
        const response = await putApiData('/Update/MailingList', body);
        setMailingInfo(response.data);
        showToast("Mailing list updated successfully.", "success");
      }catch (error){
        console.error("Error fetching data:", error);
        showToast("Failed to update mailing list.", "error");
      }

    };

    const newpage = async (nextPage, search) => {
      setSearchParams({
        query: search || "",
        page: normalizePageParam(nextPage),
      });
    };

    const saveUser = async () => {
      setOpen(false);
      setSelectedUser(null);
      setEditStatus(false);
      await fetchContacts();
    };

    const removeUser = async () => {
      const body = {
        id: selectedUser.id,
        mailing_list_id: parseInt(id, 10),
      };
    
      try {
        await putApiData("/Remove/Lists", body);
        showToast("Email removed successfully.", "success");
        setOpen(false);
        setSelectedUser(null);
        setEditStatus(false);
        await fetchContacts();
      } catch (error) {
        console.error("Error removing user:", error);
        showToast("Unable to remove this email from the list.", "error");
      }
    };
    

    const renderModalContent = () => {
      switch (editStatus) {
        case 'Add':
          return (
            <ManualAdd
              selectedUser={null}
              onSave={saveUser}
              listId={id}
            />
          );

        case 'Edit':
          return (
            <ManualAdd
              selectedUser={selectedUser}
              onSave={saveUser}
            />
          );

        case 'Delete':
          return (
            <Typography>
              Are you sure you want to remove this email from the mailing list?
            </Typography>
          );

        default:
          return null;
      }
    };

    const getModalTitle = () => {
      switch (editStatus) {
        case 'Add':
          return 'Manual Add Email';
        case 'Edit':
          return 'Edit Email';
        case 'Delete':
          return 'Remove Email';
        default:
          return '';
      }
    };




    const handleRowOpen = (user) => {
      setSelectedUser(user);
      setEditStatus('Edit');
      setOpen(true);
    };

    const handleAddRowOpen = () => {
      setSelectedUser(null);
      setEditStatus('Add');
      setOpen(true);
    };

    const handleDeleteRowOpen = (user) => {
      setSelectedUser(user);
      setEditStatus('Delete');
      setOpen(true);
    };

    const handleOpen = () =>{
        setOpen(!open)
    };

    return(
        <Box sx={{ px: { xs: 1, md: 2 }, pb: 3 }}>
          <Stack spacing={0.5} sx={{ py: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'black' }}>
              Edit Mailing List
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Update list details and manage the contacts on this mailing list.
            </Typography>
          </Stack>

          <Stack spacing={2}>
            <EditMailingInfo mailingInfo={mailingInfo} theme={theme} onSave={editMailingInfo}/>

            <div className={css.container}>
                <TableComponent
                  apiData={data}
                  progress={progress}
                  noResults={!progress && Array.isArray(data?.data) && data.data.length === 0}
                  columnNames={{
                    name: "Name",
                    email: "Email",
                    maling_list: "Mailing Lists",
                    company_name: "Company Name",
                    job_role: "Job Role",
                    time_added: "Time Added",
                  }}
                  handleAdd={handleAddRowOpen}
                  handleOpen={handleRowOpen}
                  handleDelete={handleDeleteRowOpen}
                  theme={theme}
                  mailingTable={true}
                  newpage={newpage}
                  query={query}
                  pageNumber={page}
                />
                <ModalComponent
                  title={getModalTitle()}
                  subtitle={
                    editStatus === "Delete"
                      ? "This will remove the email from this mailing list."
                      : editStatus === "Edit"
                        ? "Update the contact details for this mailing list entry."
                        : "Manually add a contact email to this mailing list."
                  }
                  open={open}
                  handleOpen={handleOpen}
                  theme={theme}
                  handleSubmit={editStatus === "Delete" ? removeUser : ""}
                  alternateSaveMessage="Remove email"
                  submitColor="error"
                  maxWidth={editStatus === "Delete" ? 480 : 560}
                >
                  {renderModalContent()}
                </ModalComponent>
            </div>
          </Stack>
        </Box>
    );
};

export default EditMalingList;
